import { defineStore } from "pinia";
import differenceWith from "lodash/differenceWith";
import unionWith from "lodash/unionWith";
import isEqual from "lodash/isEqual";
import { StoreModule } from "@/constants/store.ts";

/**
 * Represents the current state of selection.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Pinia store state cannot be generic; `unknown` would force consumers (TicketsPage, UsersPage, DevicesPage, etc.) to cast every read. `any` is bivariant and structurally safe here.
export interface ISelection<T = any> {
  /** Indicates if the user has explicitly selected "All Pages". */
  isAllPages: boolean;
  /** List of items explicitly selected (used when isAllPages is false). */
  include: T[];
  /** List of items explicitly deselected (used when isAllPages is true). */
  exclude: T[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- see ISelection rationale above
export interface SelectorState<T = any> {
  totalItems: number;
  selection: ISelection<T>;
}

export interface SelectionPayload<T> {
  items: T[];
  totalItems: number;
}

const DEFAULT_SELECTION: ISelection = {
  isAllPages: false,
  include: [],
  exclude: [],
};

export const useSelectorStore = defineStore(StoreModule.SELECTOR, {
  state: (): SelectorState => ({
    selection: { ...DEFAULT_SELECTION },
    totalItems: 0,
  }),

  actions: {
    /**
     * Sets the selection mode to "All Pages".
     * Clears specific inclusion/exclusion lists to ensure a clean state.
     */
    selectAllPages() {
      this.selection = {
        isAllPages: true,
        include: [],
        exclude: [],
      };
    },

    /**
     * Resets the selection to the default empty state.
     */
    deselectAllPages() {
      this.selection = { ...DEFAULT_SELECTION };
    },

    /**
     * Adds items to the current selection.
     *
     * If in "All Pages" mode, items are removed from the exclusion list.
     * Otherwise, items are added to the inclusion list.
     *
     * Note: This action does not automatically switch to "All Pages" mode,
     * even if the resulting selection includes the total count of items.
     *
     * @param payload - Object containing the items to select.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors ISelection<T> default; see top of file
    selectItems<T = any>({ items }: SelectionPayload<T>) {
      // Scenario 1: Handling "All Pages" mode.
      // Removes the selected items from the 'exclude' list to re-select them.
      if (this.selection.isAllPages) {
        this.selection.exclude = differenceWith(this.selection.exclude, items, isEqual);
        return;
      }

      // Scenario 2: Handling normal selection.
      // Adds items to the include array without triggering "All Pages" mode.
      this.selection.include = unionWith(this.selection.include, items, isEqual);
    },

    /**
     * Removes items from the current selection.
     *
     * If in "All Pages" mode, items are added to the exclusion list.
     * If all items are subsequently excluded, the state resets to default (empty).
     * Otherwise, items are removed from the inclusion list.
     *
     * @param payload - Object containing the items to deselect and total items count.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors ISelection<T> default; see top of file
    deselectItems<T = any>({ items, totalItems }: SelectionPayload<T>) {
      // Scenario 1: Handling "All Pages" mode.
      if (this.selection.isAllPages) {
        const newExclude = unionWith(this.selection.exclude, items, isEqual);

        // Edge Case: If the exclusion list matches the total items count,
        // the state is reset to standard "empty" rather than "All Pages except everything".
        if (newExclude.length >= totalItems) {
          this.deselectAllPages();
        } else {
          this.selection.exclude = newExclude;
        }
        return;
      }

      // Scenario 2: Handling normal selection.
      // Removes items from the include array.
      this.selection.include = differenceWith(this.selection.include, items, isEqual);
    },

    /**
     * Manually sets the selection state.
     * @param selection - The new selection object to apply.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors ISelection<T> default; see top of file
    setSelection<T = any>(selection: ISelection<T>) {
      this.selection = selection;
    },
  },
});
